import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import {
  Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
  BedDouble, Users, DollarSign, X, Check, Loader2, Building2,
  Camera, ChevronDown, ArrowLeft,
} from "lucide-react";
import { roomTypesApi, venuesApi, uploadApi, type RoomTypeResponse, type VenueResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createPortal } from "react-dom";

export const Route = createFileRoute("/admin/rooms")({
  component: RoomsPage,
});

const fmt = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(n);

type PhotoEntry = { preview: string; url: string; label: string; uploading?: boolean };

type FormState = {
  venueId: string;
  name: string;
  pricePerDay: string;
  capacity: string;
  quantity: string;
  description: string;
  amenities: string[];
  photos: PhotoEntry[];
};

const ROOM_PHOTO_SECTIONS = [
  { key: "Room / Bed",        emoji: "🛏️", required: true  },
  { key: "Bathroom",          emoji: "🚿", required: false },
  { key: "Parlor / Sitting",  emoji: "🛋️", required: false },
  { key: "Window View",       emoji: "🪟", required: false },
  { key: "Other",             emoji: "📷", required: false },
] as const;

const AMENITY_OPTIONS = [
  "Air Conditioning", "Private Bathroom", "Free WiFi", "Flat Screen TV",
  "Mini Bar", "Breakfast Included", "City View", "Balcony",
  "King Bed", "Twin Beds", "Work Desk", "Safe Box",
  "Wardrobe", "Heating", "Soundproofing", "Room Service",
];

const emptyForm = (venueId: string): FormState => ({
  venueId, name: "", pricePerDay: "", capacity: "2", quantity: "1", description: "", amenities: [], photos: [],
});

function normalizedType(type?: string | null) {
  return String(type || "").trim().toLowerCase();
}

function isEventLike(type?: string | null) {
  const clean = normalizedType(type);
  return ["eventcentre", "event centre", "event center", "event-centre", "conference hall", "open space", "open-space"].includes(clean);
}

function getInventoryNoun(type?: string | null) {
  const clean = normalizedType(type);
  if (clean === "apartment") return "unit";
  if (clean === "conference hall") return "hall";
  if (clean === "open space" || clean === "open-space") return "space";
  if (clean === "eventcentre" || clean === "event centre" || clean === "event center" || clean === "event-centre") return "space";
  if (clean === "resort") return "option";
  return "room";
}

function getInventoryTitle(type?: string | null) {
  const noun = getInventoryNoun(type);
  if (noun === "room") return "Room Types";
  if (noun === "unit") return "Units";
  if (noun === "hall") return "Halls";
  if (noun === "space") return "Spaces";
  return "Accommodation Types";
}

function getPriceSuffix(type?: string | null) {
  return isEventLike(type) ? "/event" : "/night";
}

type RoomsSearch = { venueId?: string };

export default function RoomsPage() {
  const { user } = useAuth();
  const search = Route.useSearch() as RoomsSearch;

  /* All manager venues, sorted A-Z */
  const [venues, setVenues] = useState<VenueResponse[]>([]);
  const [loadingVenues, setLoadingVenues] = useState(true);

  /* Active tab = currently selected venue id */
  const [activeVenueId, setActiveVenueId] = useState<string | undefined>(search.venueId);

  useEffect(() => {
    if (!user?.id) return;
    setLoadingVenues(true);
    venuesApi.myVenues(user.id)
      .then(res => {
        const sorted = [...res.data].sort((a, b) => a.name.localeCompare(b.name));
        setVenues(sorted);
        if (!activeVenueId && sorted.length > 0) setActiveVenueId(String(sorted[0].id));
      })
      .catch(() => toast.error("Failed to load venues"))
      .finally(() => setLoadingVenues(false));
  }, [user?.id]);

  /* Rooms for the active venue */
  const [roomsByVenue, setRoomsByVenue] = useState<Record<string, RoomTypeResponse[]>>({});
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadRooms = useCallback(async (vid: string) => {
    if (roomsByVenue[vid] !== undefined) return; // already loaded
    setLoadingRooms(true);
    try {
      const data = await roomTypesApi.list(vid);
      setRoomsByVenue(m => ({ ...m, [vid]: data }));
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to load room types.";
      toast.error(msg);
      setRoomsByVenue(m => ({ ...m, [vid]: [] }));
    } finally {
      setLoadingRooms(false);
    }
  }, [roomsByVenue]);

  useEffect(() => {
    if (activeVenueId) loadRooms(activeVenueId);
  }, [activeVenueId]);

  const activeRooms = activeVenueId ? (roomsByVenue[activeVenueId] ?? []) : [];
  const activeVenue = venues.find(v => String(v.id) === activeVenueId);
  const inventoryTitle = getInventoryTitle(activeVenue?.type);
  const inventoryNoun = getInventoryNoun(activeVenue?.type);

  /* ── Form ── */
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<RoomTypeResponse | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm(activeVenueId ?? ""));
  const [saving, setSaving] = useState(false);
  const [venueDropOpen, setVenueDropOpen] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(activeVenueId ?? ""));
    setShowForm(true);
  };
  const openEdit = (r: RoomTypeResponse) => {
    setEditing(r);
    setForm({
      venueId: activeVenueId ?? "",
      name: r.name,
      pricePerDay: String(r.pricePerDay),
      capacity: String(r.capacity),
      quantity: String(r.quantity || 1),
      description: r.description ?? "",
      amenities: r.amenities ?? [],
      photos: (r.images ?? []).map(img => ({
        preview: img.imageUrl,
        url: img.imageUrl,
        label: img.label ?? "Other",
      })),
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const toggleAmenity = (a: string) =>
    setForm(f => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter(x => x !== a) : [...f.amenities, a],
    }));

  const handleSectionPhotoUpload = async (label: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    e.target.value = "";
    for (const file of files) {
      const preview = URL.createObjectURL(file);
      setForm(prev => ({ ...prev, photos: [...prev.photos, { preview, url: "", label, uploading: true }] }));
      try {
        const result = await uploadApi.image(file);
        setForm(prev => ({
          ...prev,
          photos: prev.photos.map(p => p.preview === preview ? { ...p, url: result.url, uploading: false } : p),
        }));
      } catch {
        toast.error(`Failed to upload ${file.name}`);
        setForm(prev => ({ ...prev, photos: prev.photos.filter(p => p.preview !== preview) }));
      }
    }
  };

  const removePhoto = (preview: string) =>
    setForm(prev => ({ ...prev, photos: prev.photos.filter(p => p.preview !== preview) }));

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(`${formInventoryNoun.charAt(0).toUpperCase() + formInventoryNoun.slice(1)} name is required`);
    const price = parseFloat(form.pricePerDay);
    if (isNaN(price) || price <= 0) return toast.error("Enter a valid price");
    const cap = parseInt(form.capacity);
    if (isNaN(cap) || cap < 1) return toast.error("Capacity must be at least 1");
    const quantity = parseInt(form.quantity);
    if (isNaN(quantity) || quantity < 1) return toast.error(`Number of ${formInventoryNoun}s must be at least 1`);
    const vid = form.venueId || activeVenueId;
    if (!vid) return toast.error("Select a venue first");

    if (form.photos.some(p => p.uploading || !p.url)) {
      return toast.error("Please wait for all photos to finish uploading.");
    }

    const images = form.photos.map((p, i) => ({ imageUrl: p.url, label: p.label, sortOrder: i }));

    setSaving(true);
    try {
      if (editing) {
        const updated = await roomTypesApi.update(vid, editing.id, {
          name: form.name.trim(),
          pricePerDay: price,
          capacity: cap,
          quantity,
          description: form.description.trim() || undefined,
          images,
          amenities: form.amenities,
        });
        setRoomsByVenue(m => ({ ...m, [vid]: (m[vid] ?? []).map(r => r.id === editing.id ? updated : r) }));
        toast.success(`${formInventoryTitle} updated`);
      } else {
        const created = await roomTypesApi.create(vid, {
          name: form.name.trim(),
          pricePerDay: price,
          capacity: cap,
          quantity,
          description: form.description.trim() || undefined,
          images,
          amenities: form.amenities.length > 0 ? form.amenities : undefined,
        });
        setRoomsByVenue(m => ({ ...m, [vid]: [...(m[vid] ?? []), created] }));
        // Switch tab to the venue this room was added to
        setActiveVenueId(vid);
        toast.success(`${formInventoryTitle} added`);
      }
      closeForm();
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : `Failed to save ${formInventoryNoun} type.`;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r: RoomTypeResponse) => {
    if (!activeVenueId) return;
    setTogglingId(r.id);
    try {
      const updated = await roomTypesApi.toggleActive(activeVenueId, r.id, !r.isActive);
      setRoomsByVenue(m => ({ ...m, [activeVenueId]: (m[activeVenueId] ?? []).map(x => x.id === r.id ? updated : x) }));
      toast.success(updated.isActive ? "Room marked Available" : "Room marked Booked");
    } catch {
      setRoomsByVenue(m => ({ ...m, [activeVenueId]: (m[activeVenueId] ?? []).map(x => x.id === r.id ? { ...x, isActive: !x.isActive } : x) }));
    } finally {
      setTogglingId(null);
    }
  };

  const deleteRoom = async (r: RoomTypeResponse) => {
    if (!activeVenueId || !confirm(`Delete "${r.name}"?`)) return;
    setDeletingId(r.id);
    try {
      await roomTypesApi.delete(activeVenueId, r.id);
      setRoomsByVenue(m => ({ ...m, [activeVenueId]: (m[activeVenueId] ?? []).filter(x => x.id !== r.id) }));
      toast.success("Room type deleted");
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to delete room type.";
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  /* ── Loading venues ── */
  if (loadingVenues) {
    return (
      <div className="flex h-60 items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-5 animate-spin" /> Loading your venues…
      </div>
    );
  }

  if (venues.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 py-24 text-center">
        <Building2 className="mb-4 size-14 text-muted-foreground/40" />
        <h2 className="text-xl font-bold">No Venues Yet</h2>
        <p className="mt-2 max-w-sm text-sm text-muted-foreground">
          List your first property to start managing room types.
        </p>
        <Link to="/list-your-property">
          <button className="mt-5 rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90 transition-opacity">
            List Your Property
          </button>
        </Link>
      </div>
    );
  }

  const available = activeRooms.filter(r => r.isActive).length;
  const booked = activeRooms.length - available;
  const formVenue = venues.find(v => String(v.id) === form.venueId);
  const formVenueName = formVenue?.name ?? "Select venue";
  const formInventoryNoun = getInventoryNoun(formVenue?.type ?? activeVenue?.type);
  const formInventoryTitle = getInventoryTitle(formVenue?.type ?? activeVenue?.type);

  return (
    <div className="mx-auto max-w-6xl animate-fade-up space-y-6 p-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/listings"
            className="flex size-10 items-center justify-center rounded-xl border border-border/80 bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title="Back to My Listings"
          >
            <ArrowLeft className="size-5" strokeWidth={2.5} />
          </Link>
          <div>
            <h1 className="font-display text-xl font-bold">{inventoryTitle}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage {inventoryNoun} types across all your properties.
            </p>
          </div>
        </div>
        <Button onClick={openCreate} className="gap-2 rounded-xl h-10">
          <Plus className="size-4" /> Add {inventoryNoun === "option" ? "Type" : inventoryNoun.charAt(0).toUpperCase() + inventoryNoun.slice(1)}
        </Button>
      </div>

      {/* ── Venue tabs (sorted A-Z) ── */}
      <div className="flex flex-wrap gap-2 border-b border-border/60 pb-3">
        {venues.map(v => (
          <button
            key={v.id}
            onClick={() => setActiveVenueId(String(v.id))}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
              String(v.id) === activeVenueId
                ? "bg-primary text-primary-foreground shadow-sm"
                : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
          >
            {v.name}
          </button>
        ))}
      </div>

      {/* ── Summary pills ── */}
      <div className="flex flex-wrap gap-3">
        {[
          { label: `Total ${inventoryNoun}s`, value: activeRooms.length, color: "bg-muted text-foreground" },
          { label: "Available",   value: available,           color: "bg-emerald-100 text-emerald-700" },
          { label: "Booked / Inactive", value: booked,        color: "bg-rose-100 text-rose-700" },
        ].map(s => (
          <div key={s.label} className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold ${s.color}`}>
            <span className="text-base font-bold">{s.value}</span>
            <span>{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Room cards ── */}
      {loadingRooms ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[1,2,3].map(i => <div key={i} className="shimmer h-44 rounded-2xl" />)}
        </div>
      ) : activeRooms.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border py-20 text-center">
          <BedDouble className="mb-3 size-12 text-muted-foreground/40" />
          <p className="font-semibold text-muted-foreground">No {inventoryNoun} types yet for {activeVenue?.name}</p>
          <button
            onClick={openCreate}
            className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
          >
            + Add First {inventoryNoun === "option" ? "Type" : inventoryNoun.charAt(0).toUpperCase() + inventoryNoun.slice(1)}
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {activeRooms.map((r, i) => (
            <div
              key={r.id}
              className={`relative flex flex-col rounded-2xl border-2 bg-card p-5 transition-shadow hover:shadow-md ${
                r.isActive ? "border-border" : "border-rose-200 bg-rose-50/30"
              }`}
            >
              <span className={`absolute right-4 top-4 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                r.isActive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
              }`}>
                {r.isActive ? "Available" : "Booked"}
              </span>
              <div className="mb-3 grid size-11 place-items-center rounded-xl bg-primary/10 text-primary">
                <BedDouble className="size-5" />
              </div>
              <h3 className="pr-16 text-lg font-bold leading-tight truncate" title={r.name}>{r.name}</h3>
              {r.description && <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{r.description}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="flex items-center gap-1 font-semibold text-primary">
                  <DollarSign className="size-3.5" />{fmt(r.pricePerDay)}<span className="text-xs font-normal text-muted-foreground">{getPriceSuffix(activeVenue?.type)}</span>
                </span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Users className="size-3.5" />{r.capacity} guests
                </span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <BedDouble className="size-3.5" />{r.quantity} {inventoryNoun}{r.quantity !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="mt-4 flex items-center gap-2 border-t border-border pt-3">
                <button
                  onClick={() => toggleActive(r)}
                  disabled={togglingId === r.id}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-accent"
                >
                  {togglingId === r.id
                    ? <Loader2 className="size-4 animate-spin" />
                    : r.isActive
                      ? <ToggleRight className="size-4 text-emerald-600" />
                      : <ToggleLeft className="size-4 text-muted-foreground" />}
                  {r.isActive ? "Mark Booked" : "Mark Available"}
                </button>
                <div className="ml-auto flex gap-1">
                  <button onClick={() => openEdit(r)} className="rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors" title="Edit">
                    <Pencil className="size-4" />
                  </button>
                  <button onClick={() => deleteRoom(r)} disabled={deletingId === r.id} className="rounded-lg p-2 text-muted-foreground hover:bg-rose-100 hover:text-rose-600 transition-colors" title="Delete">
                    {deletingId === r.id ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Centered modal form ── */}
      {showForm && typeof document !== "undefined" && createPortal(
        <>
          <div className="fixed inset-0 z-40 bg-foreground/30 backdrop-blur-sm transition-opacity" onClick={closeForm} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <div className="relative flex w-full max-w-2xl flex-col rounded-2xl border border-border/60 bg-card shadow-2xl oyo-modal-content">
              {/* Modal header */}
              <div className="flex items-center gap-4 border-b border-border/60 px-6 py-5">
                <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                  <BedDouble className="size-5" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold">{editing ? `Edit ${formInventoryTitle}` : `Add ${formInventoryTitle}`}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {editing ? `Update this ${formInventoryNoun}'s details below` : `Configure a new ${formInventoryNoun} type for your property`}
                  </p>
                </div>
                <button
                  onClick={closeForm}
                  className="grid size-9 place-items-center rounded-xl text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  title="Close"
                  aria-label="Close modal"
                >
                  <X className="size-5" />
                </button>
              </div>

              <form onSubmit={onSave} className="flex flex-1 flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

                  {/* Venue selector (only on create, and only when multiple venues) */}
                  {!editing && venues.length > 1 && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-bold">Property <span className="text-rose-500">*</span></Label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setVenueDropOpen(o => !o)}
                          className="flex h-11 w-full items-center justify-between rounded-xl border border-border/80 bg-background px-4 text-sm font-medium hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                        >
                          <span>{formVenueName}</span>
                          <ChevronDown className="size-4 text-muted-foreground" />
                        </button>
                        {venueDropOpen && (
                          <div className="absolute left-0 right-0 top-12 z-10 max-h-48 overflow-y-auto rounded-xl border border-border/80 bg-card shadow-lg">
                            {venues.map(v => (
                              <button
                                key={v.id}
                                type="button"
                                onClick={() => { setForm(f => ({ ...f, venueId: String(v.id) })); setVenueDropOpen(false); }}
                                className={`flex w-full items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent first:rounded-t-xl last:rounded-b-xl ${form.venueId === String(v.id) ? "font-semibold text-primary" : ""}`}
                              >
                                <Building2 className="size-4 shrink-0 text-muted-foreground" />
                                {v.name}
                                <span className="ml-auto text-xs text-muted-foreground">{v.location}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Room name */}
                  <div className="space-y-1.5">
                    <Label className="text-sm font-bold">{formInventoryNoun.charAt(0).toUpperCase() + formInventoryNoun.slice(1)} name <span className="text-rose-500">*</span></Label>
                    <Input
                      required placeholder="e.g. Deluxe Suite, Standard Room, Executive Suite"
                      value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="h-11 rounded-xl"
                    />
                  </div>

                  {/* Price + Capacity row */}
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-bold">Price {getPriceSuffix(formVenue?.type ?? activeVenue?.type)} (NGN) <span className="text-rose-500">*</span></Label>
                      <Input
                        required type="number" min="0" step="500"
                        placeholder="e.g. 25000"
                        value={form.pricePerDay} onChange={e => setForm(f => ({ ...f, pricePerDay: e.target.value }))}
                        className="h-11 rounded-xl"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-bold">Capacity <span className="text-rose-500">*</span></Label>
                      <Input
                        required type="number" min="1" max="50"
                        placeholder="2"
                        value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))}
                        className="h-11 rounded-xl"
                      />
                      <p className="text-[11px] text-muted-foreground">Max guests {getPriceSuffix(formVenue?.type ?? activeVenue?.type)}</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-bold">Number of {formInventoryNoun}s <span className="text-rose-500">*</span></Label>
                      <Input
                        required type="number" min="1" max="500"
                        placeholder="1"
                        value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                        className="h-11 rounded-xl"
                      />
                      <p className="text-[11px] text-muted-foreground">Inventory count for this {formInventoryNoun} type</p>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-1.5">
                    <Label className="text-sm font-bold">Description</Label>
                    <textarea
                      rows={3}
                      placeholder={`Describe this ${formInventoryNoun} type - what makes it special, what it includes...`}
                      value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      className="w-full rounded-xl border border-border/80 bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                    />
                  </div>

                  {/* Amenities */}
                  <div className="space-y-2">
                    <Label className="text-sm font-bold">{formInventoryNoun.charAt(0).toUpperCase() + formInventoryNoun.slice(1)} amenities</Label>
                    <p className="text-[11px] text-muted-foreground">Select all that apply to this {formInventoryNoun} type.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {AMENITY_OPTIONS.map(a => {
                        const on = form.amenities.includes(a);
                        return (
                          <button
                            key={a}
                            type="button"
                            onClick={() => toggleAmenity(a)}
                            className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-medium transition-all text-left ${
                              on
                                ? "border-primary bg-primary/10 text-primary shadow-sm"
                                : "border-border/60 bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground"
                            }`}
                          >
                            <span className={`size-3.5 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${on ? "border-primary bg-primary" : "border-muted-foreground/40"}`}>
                              {on && <Check className="size-2.5 text-white" strokeWidth={3} />}
                            </span>
                            {a}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Photo upload — per section */}
                  <div className="space-y-2">
                    <Label className="text-sm font-bold">{formInventoryNoun.charAt(0).toUpperCase() + formInventoryNoun.slice(1)} photos</Label>
                    <p className="text-[11px] text-muted-foreground">
                      Add photos for each part of the {formInventoryNoun}. Tap the camera icon next to a section to upload.
                    </p>
                    <div className="space-y-2">
                      {ROOM_PHOTO_SECTIONS.map(section => {
                        const sectionPhotos = form.photos.filter(p => p.label === section.key);
                        return (
                          <div key={section.key} className="rounded-xl border border-border/40 bg-accent/10 p-3 space-y-2">
                            <div className="flex items-center gap-3">
                              <span className="text-base shrink-0">{section.emoji}</span>
                              <div className="flex-1 min-w-0">
                                <span className="text-xs font-semibold">{section.key}</span>
                                {section.required
                                  ? <span className="ml-1.5 text-[9px] bg-primary/10 text-primary py-0.5 px-1.5 rounded-full font-semibold">Required</span>
                                  : <span className="ml-1.5 text-[9px] text-muted-foreground">Optional</span>
                                }
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {sectionPhotos.length > 0 && (
                                  <span className="text-[10px] text-muted-foreground">
                                    {sectionPhotos.length} photo{sectionPhotos.length !== 1 ? "s" : ""}
                                  </span>
                                )}
                                <label className="flex items-center justify-center size-9 rounded-lg border border-dashed border-border/60 hover:border-primary/40 bg-background hover:bg-primary/5 cursor-pointer transition-all">
                                  <Camera className="size-3.5 text-muted-foreground" />
                                  <input
                                    type="file"
                                    title="Upload photos"
                                    multiple
                                    accept="image/jpeg,image/png,image/webp"
                                    className="hidden"
                                    onChange={e => handleSectionPhotoUpload(section.key, e)}
                                  />
                                </label>
                              </div>
                            </div>
                            {sectionPhotos.length > 0 && (
                              <div className="flex flex-wrap gap-2 pt-1">
                                {sectionPhotos.map(photo => (
                                  <div key={photo.preview} className="relative size-14 rounded-lg overflow-hidden border border-border group/img shrink-0">
                                    <img src={photo.preview} alt={photo.label} className="size-full object-cover" />
                                    {photo.uploading && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                        <Loader2 className="size-3 animate-spin text-white" />
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => removePhoto(photo.preview)}
                                      className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 group-hover/img:opacity-100 transition-opacity"
                                      aria-label="Remove"
                                    >
                                      <X className="size-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>

                {/* Footer actions — always visible */}
                <div className="shrink-0 border-t border-border/60 bg-card/80 px-6 py-4 rounded-b-2xl">
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={closeForm}>Cancel</Button>
                    <Button type="submit" disabled={saving} className="flex-1 gap-2 rounded-xl">
                      {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                      {editing ? "Save Changes" : `Add ${formInventoryTitle}`}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
